import {
  createReducer,
  on
} from "@ngrx/store";
import { getInitialState } from "./initial.state";
import { saveAccessToken } from "../actions/user/save-access-token";
import { setPermissions } from "../actions/user/set-permissions";
import { signOut } from "../actions/user/signout";
import { initializeTokenValue } from "../actions/app/initialize-token-value";
import { initializedWithNoToken } from "../actions/app/initialized-with-no-token";
import { rejectUserDetails } from "../actions/user/reject-user-details";
import { resolveUserDetails } from "../actions/user/resolve-user-details";
import { resolveCurrentProject } from "../actions/account/resolve-current-project";
import { initializedWithToken } from "../actions/app/initialized-with-token";
import { settingsRenameUser } from "../actions/app/rename-user";
import { rejectCurrentProject } from "../actions/account/reject-current-project";
import { initializedWithError } from "../actions/app/initialized-with-error";
import { onboardUser } from "../actions/user/onboard-user";
import { selectProject } from "../actions/account/select-project";
import { resolveAssociatedProjects } from "../actions/account/resolve-associated-projects";
import { rejectAssociatedProjects } from "../actions/account/reject-associated-projects";
import { fetchAssociatedProjects } from "../actions/account/fetch-associated-projects";
import { resolveFavoriteProjects } from "../actions/account/resolve-favorite-projects";
import { rejectFavoriteProjects } from "../actions/account/reject-favorite-projects";
import { fetchFavoriteProjects } from "../actions/account/fetch-favorite-projects";
import { openLoader } from "../actions/loader/open-loader";
import { closeLoader } from "../actions/loader/close-loader";
import { ProjectType } from "src/app/features/auth/types/project-type.type";

const initialState = getInitialState();

export const appStateReducer = createReducer(
  initialState,

  on(onboardUser, (state, { token }) => ({
    ...state,
    accessToken: token,
    isError: false,
    initialized: false,
    isAuthenticated: false,
    loader: {
      ...initialState.loader,
      loading: true,
    },
    userDetails: {
      ...state.userDetails,
      isLoading: true,
      isError: false,
      error: undefined,
    },
    project: {
      ...state.project,
      isLoading: true,
      isError: false,
      error: undefined,
    },
    client: {
      ...state.client,
      isLoading: true,
      isError: false,
      error: undefined,
    },
  })),

  on(resolveCurrentProject, (state, { project, client }) => ({
    ...state,
    client: {
      ...state.client,
      data: client,
      isLoading: false,
      isError: false,
      error: undefined,
    },
    project: {
      ...state.project,
      data: project,
      isLoading: false,
      isError: false,
      error: undefined,
    },
  })),

  on(rejectCurrentProject, (state, { error }) => ({
    ...state,
    client: {
      ...state.client,
      data: undefined!,
      isLoading: false,
      isError: true,
      error,
    },
    project: {
      ...state.project,
      data: undefined!,
      isLoading: false,
      isError: true,
      error,
    },
  })),

  on(rejectUserDetails, (state, { error }) => ({
    ...state,
    userDetails: {
      ...state.userDetails,
      data: undefined!,
      isLoading: false,
      isError: true,
      error,
    },
  })),

  on(resolveUserDetails, (state, { user }) => ({
    ...state,
    userDetails: {
      ...state.userDetails,
      data: {
        ...user,
        accountId: state.userDetails.data.accountId, // temporary?
      },
      isLoading: false,
      isError: false,
      error: undefined,
    },
  })),

  on(initializeTokenValue, (state, {
    token,
    userId,
    accountType,
    accountId,
    storedAccountId,
    accountIds,
    role,
  }) => ({
    ...state,
    accessToken: token,
    accountType,
    accountIds,
    role,
    // automatically select the account if there's only one available, otherwise wait for user to select
    selectedAccountId: accountIds.length === 1 ? accountIds[0] : (storedAccountId ?? undefined),
    userDetails: {
      ...state.userDetails,
      data: {
        ...state.userDetails.data,
        accountId: accountIds.length === 1 ? accountIds[0] : accountId,
        id: userId,
      },
      isLoading: true,
      isError: false,
      error: undefined,
    },
  })),

  on(selectProject, (state, { id }) => {
    const selectedAccount = state.associatedAccounts.data?.find(
      (account) => account.accountId === id
    );
    const accountType = (selectedAccount?.accountType ??
      state.accountType) as ProjectType;
    return {
      ...state,
      selectedAccountId: id,
      accountType,
      userDetails: {
        ...state.userDetails,
        data: {
          ...state.userDetails.data,
          accountId: id,
        },
      },
      project: {
        ...state.project,
        isLoading: true,
      },
      client: {
        ...state.client,
        isLoading: true,
      },
    };
  }),

  on(initializedWithNoToken, (state) => ({
    ...state,
    initialized: true,
    isAuthenticated: false,
    accessToken: null!,
    refreshToken: null!,
    loader: {
      ...initialState.loader,
      loading: false,
    },
    isError: false,
  })),

  on(initializedWithToken, (state) => ({
    ...state,
    accountType: (
      state.associatedAccounts.data.find(
        (account) => account.accountId === state.selectedAccountId
      )?.accountType ?? state.accountType
    ) as ProjectType,
    isError: false,
    initialized: true,
    isAuthenticated: true,
    loader: {
      ...initialState.loader,
      loading: false,
    },
  })),

  on(initializedWithError, (state) => ({
    ...state,
    initialized: true,
    isAuthenticated: true,
    loader: {
      ...initialState.loader,
      loading: false,
    },
    isError: true,
  })),

  on(settingsRenameUser, (state, {
    firstName,
    lastName,
    avatar,
    mobileNumber,
  }) => ({
    ...state,
    userDetails: {
      ...state.userDetails,
      data: {
        ...state.userDetails.data,
        firstName,
        lastName,
        avatar,
        mobileNumber,
      },
    },
  })),

  on(resolveAssociatedProjects, (state, { projects }) => ({
    ...state,
    associatedAccounts: {
      ...state.associatedAccounts,
      data: projects,
      isLoading: false,
      isError: false,
      error: undefined,
    },
  })),

  on(rejectAssociatedProjects, (state, { error }) => ({
    ...state,
    associatedAccounts: {
      ...state.associatedAccounts,
      data: [],
      isLoading: false,
      isError: true,
      error,
    },
  })),

  on(fetchAssociatedProjects, (state) => ({
    ...state,
    associatedAccounts: {
      ...state.associatedAccounts,
      isLoading: true,
      isError: false,
      error: undefined,
    },
  })),

  on(resolveFavoriteProjects, (state, { projects }) => ({
    ...state,
    favoriteProjects: {
      ...state.favoriteProjects,
      data: projects,
      isLoading: false,
      isError: false,
      error: undefined,
    },
  })),

  on(rejectFavoriteProjects, (state, { error }) => ({
    ...state,
    favoriteProjects: {
      ...state.favoriteProjects,
      data: [],
      isLoading: false,
      isError: true,
      error,
    },
  })),

  on(fetchFavoriteProjects, (state) => ({
    ...state,
    favoriteProjects: {
      ...state.favoriteProjects,
      isLoading: true,
      isError: false,
      error: undefined,
    },
  })),

  on(saveAccessToken, (state, { accessToken, refreshToken }) => ({
    ...state,
    accessToken,
    refreshToken,
  })),

  on(setPermissions, (state, { permissions, isAuthenticated }) => ({
    ...state,
    permissions,
    isAuthenticated,
    loader: {
      ...initialState.loader,
      loading: false,
    },
    initialized: true,
  })),

  on(openLoader, (state, { message, customImage }) => ({
    ...state,
    loader: {
      loading: true,
      message: message ?? initialState.loader.message,
      customImage,
    },
  })),

  on(closeLoader, (state) => ({
    ...state,
    loader: {
      ...initialState.loader,
      loading: false,
    },
  })),

  on(signOut, () => ({
    ...initialState,
    initialized: false,
    loader: {
      ...initialState.loader,
      loading: false,
    },
  }))
);
